<template>
  <div class="user-table table-wrapper">
    <header class="table-header">
      <div class="row justify-content-between align-items-center mb-2">
        <div class="table-title d-flex align-items-center col">
          <h1 class="table-title text-left mb-0">Users List</h1>
        </div>

        <div class="btn-container col">
          <b-button variant="success" size="sm" class="d-block ml-auto" v-b-modal="'user-add-modal'" :disabled="isTableLoading"> Add User </b-button>
        </div>
      </div>

      <div class="table-helpers row justify-content-between align-items-center">
        <b-form-group
          class="table-page-size align-items-center col-12 col-md-4 col-lg-3 col-xl-3 mb-2 mb-md-0"
          label="Items per page"
          label-for="select-table-page-size"
          label-cols="6"
          label-cols-xl="5"
          label-class="table-page-size-label col-form-label-sm"
        >
          <b-form-select id="select-table-page-size" class="col pr-0 mr-0 float-right float-md-left" v-model="pageSize" :options="pageSizeOpts"></b-form-select>
        </b-form-group>
        <!-- <div class="form-group row justify-content-between justify-content-md-start mb-md-0">
            <label for="select-table-page-size" class="col-6 col-xl-7 pr-0 mb-0 col-form-label-sm">Items per page</label>
          </div> -->

        <b-form-group
          class="table-search align-items-center text-left text-md-right col-12 col-md-6 col-lg-5 mb-0"
          label-cols="3"
          label="Search"
          label-for="input-table-search"
          label-class="table-search-label col-form-label-sm"
        >
          <b-form-input id="input-table-search" type="text" placeholder="Search on any field..." v-model="filter" debounce="500" trim></b-form-input>
        </b-form-group>
      </div>
    </header>

    <b-alert variant="danger" :show="errors.length > 0" class="mt-3 mb-2">{{ errors[0] }}</b-alert>

    <b-table
      responsive
      sticky-header
      outlined
      show-empty
      class="table-container"
      :id="`table-users`"
      ref="table"
      :items="myProvider"
      :fields="fields"
      :busy.sync="isTableLoading"
      :currentPage="pageIndex"
      :perPage="pageSize"
      :filter="filter"
      :sortBy.sync="sortBy"
      :sortDesc.sync="sortDesc"
      @refreshed.once="isTableInitialized = true"
    >
      <template v-slot:head()="data">
        <div class="text-center">{{ titleCase(data.label) }}</div>
      </template>

      <template v-slot:cell()="data">
        <div class="text-center">{{ normalizeValue(data.value, data.field) }}</div>
      </template>

      <template v-slot:cell(username)="data">
        <div class="text-center">
          <b-badge v-if="isCurrentUser(data.item)" variant="success" class="mr-1">YOU</b-badge>
          {{ data.value }}
        </div>
      </template>

      <template v-slot:cell(roles)="data">
        <div class="text-center">
          <b-badge v-for="role of data.value" :key="role" :variant="role === 'admin' ? 'danger' : 'warning'" class="mx-1 py-1 px-2" style="font-size: 12px;">
            {{ role }}
          </b-badge>
        </div>
      </template>

      <template v-slot:cell(action)="data">
        <div class="text-center">
          <b-dropdown variant="primary" size="sm" @show="selectedUser = data.item">
            <template v-slot:button-content>
              <span>Edit User</span>
            </template>
            <b-dropdown-item @click="onClickEditRoles()" v-if="!isCurrentUser(data.item)">Edit Roles</b-dropdown-item>
            <b-dropdown-item @click="onClickChangePassword()">
              {{ isCurrentUser(data.item) ? 'Change Your Password' : 'Change Password' }}
            </b-dropdown-item>
            <b-dropdown-item v-b-modal="'user-delete-modal'" v-if="!isCurrentUser(data.item)" variant="danger">Delete User</b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

      <template v-slot:table-busy>
        <div class="table-overlay text-center text-danger">
          <b-spinner class="table-spinner"></b-spinner>
        </div>
      </template>

      <template v-slot:empty>
        <h5 class="text-center">No records to display.</h5>
      </template>

      <template v-slot:emptyfiltered>
        <h5 class="text-center">There are no records matching your search.</h5>
      </template>
    </b-table>

    <footer class="table-footer">
      <div class="table-pagination row justify-content-between align-items-center">
        <span class="table-pagination-range col">
          {{ currentPageRange() }}
        </span>

        <b-pagination
          v-if="isTableInitialized"
          class="table-pagination-control"
          v-model="pageIndex"
          :total-rows="totalRecords"
          :per-page="pageSize"
          aria-controls="table-users"
          aria-label="User list pagination'"
        ></b-pagination>
      </div>
    </footer>

    <UserEditModal
      v-if="selectedUser"
      modalId="table-user-edit-modal"
      :user="selectedUser"
      :roles="roles"
      :fieldToEdit="fieldToEdit"
      @editedUser="onEditedUser"
      :isCurrentUser="isCurrentUser(selectedUser)"
    />
    <UserAddModal v-if="roles" :roles="roles" @addedUser="onAddedUser" />
    <UserDeleteModal v-if="selectedUser" :user="selectedUser" @deletedUser="onDeletedUser" />
  </div>
</template>

<script>
import UserEditModal from './AdminUserEditModal';
import UserAddModal from './AdminUserAddModal';
import UserDeleteModal from './AdminUserDeleteModal';

export default {
  components: { UserEditModal, UserAddModal, UserDeleteModal },

  data() {
    return {
      isTableInitialized: false, // Needed for pagination to initialize correctly
      isTableLoading: false,
      pageIndex: parseInt(this.$router.currentRoute.query.pgIdx) || 1,
      pageSize: parseInt(this.$router.currentRoute.query.pgSz) || 10,
      pageSizeOpts: [
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 100, text: '100' }
      ],
      filter: this.$router.currentRoute.query.filter || '',
      sortBy: '',
      sortDesc: false,
      totalRecords: 0,
      fields: [
        /* id,  */
        { key: 'username', label: 'Username', sortable: true, stickyColumn: true },
        { key: 'firstName', label: 'First Name', sortable: true },
        { key: 'lastName', label: 'Last Name', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'lastLogin', label: 'Last Login', type: 'date', sortable: true },
        { key: 'createdAt', label: 'Created At', type: 'date', sortable: true },
        'roles',
        'action'
      ],
      errors: [],
      messages: [],
      roles: [],
      fieldToEdit: 'roles',
      selectedUser: null
    };
  },

  methods: {
    /** Display range of data currently being shown */
    currentPageRange() {
      if (this.totalRecords === 0) {
        return 'No records were found';
      }
      let firstRecord = (this.pageIndex - 1) * this.pageSize + 1;
      let lastRecord = this.totalRecords < firstRecord + this.pageSize - 1 ? this.totalRecords : firstRecord + this.pageSize - 1;

      return `Showing ${firstRecord.toLocaleString('en')} - ${lastRecord.toLocaleString('en')} of ${this.totalRecords.toLocaleString('en')} items`;
    },

    titleCase(value) {
      return value.replace(/_/g, ' ').replace(/\w\S*/g, function(txt) {
        if (/(id|dob)/gi.test(txt)) return txt.toUpperCase();
        if (/dt/gi.test(txt)) return 'Date';
        if (/num/gi.test(txt)) return '#';
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });
    },

    normalizeValue(value, col) {
      if (!value) {
        return '—';
      }
      if (col.type === 'date') {
        const dt = this.$moment(value).format('h:mm A D MMM, YYYY');
        return dt.toLowerCase() === 'invalid date' ? null : dt;
      }
      return value;
    },

    isCurrentUser(row) {
      return this.$store.state.userId === row.id;
    },

    /** Fetch data for table from API resource
     *  @param tblCtx Table context
     */
    async myProvider(tblCtx) {
      this.errors = []; // Reset errors
      const url = '/users';

      // Generate query parameters
      const queryParams = {};
      queryParams.pgIdx = tblCtx.currentPage;
      queryParams.pgSz = tblCtx.perPage;
      if (tblCtx.filter) queryParams.filter = tblCtx.filter;
      if (tblCtx.sortBy) {
        const sort = (tblCtx.sortDesc ? '-' : '+') + tblCtx.sortBy;
        queryParams.srt = sort;
      }

      let items = [];
      try {
        this.$router.replace({ path: '/admin', query: queryParams }).catch(() => {
          /* Avoid redundant navigation error */
        });
        // Get records
        const response = await this.$http.get(url, { params: queryParams });
        const { rowCount, users, roles } = response.data;

        this.totalRecords = rowCount;
        this.roles = roles;
        items = users;
        // console.log(users);
      } catch (err) {
        if (!err.response) {
          this.errors.push('Error: A network error occurred.');
        } else {
          if (Array.isArray(err.response.data.message)) {
            this.errors = err.response.data.message;
          } else {
            this.errors.push(err.response.data.message || 'Something went wrong while trying to get user records.');
          }
        }
      }

      return items;
    },

    openEditModal() {
      this.$bvModal.show('table-user-edit-modal');
    },

    onClickEditRoles() {
      this.fieldToEdit = 'roles';
      this.openEditModal();
    },

    onClickChangePassword() {
      this.fieldToEdit = 'password';
      this.openEditModal();
    },

    onAddedUser(user) {
      this.$refs.table.refresh();
      this.$bvToast.toast(`Successfully created user ${user.username}.`, {
        title: `User added`,
        variant: 'info',
        solid: true,
        autoHideDelay: 3000
      });
    },

    onEditedUser({ user, editFunction }) {
      this.$refs.table.refresh();
      if (editFunction === 'roles') {
        this.$bvToast.toast(`Successfully changed user roles for ${user.username}.`, {
          title: `User roles edited`,
          variant: 'info',
          solid: true,
          autoHideDelay: 3000
        });
      } else if (editFunction === 'password') {
        this.$bvToast.toast(`Successfully changed password for ${user.username}.`, {
          title: `User password changed`,
          variant: 'info',
          solid: true,
          autoHideDelay: 3000
        });
      }
    },

    onDeletedUser(user) {
      this.$refs.table.refresh();
      this.$bvToast.toast(`Successfully deleted user ${user.username}.`, {
        title: `User removed`,
        variant: 'info',
        solid: true,
        autoHideDelay: 3000
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/styles/custom.scss';

.table-container ::v-deep.table.b-table[aria-busy='true'] {
  opacity: 1 !important;
}

.table-container ::v-deep.table.b-table[aria-busy='true'] .table-overlay {
  position: absolute;
  background-color: rgba(0, 0, 0, 0.75);
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 10; /* make sure spinner is over table */
}

.table-container ::v-deep.table.b-table[aria-busy='true'] tbody tr {
  height: 400px;
}

.table-container ::v-deep.table.b-table[aria-busy='true'] .table-overlay .table-spinner {
  color: var(--primary);
  position: absolute;
  left: calc(50% - 50px);
  top: calc(50% - 50px);
  height: 100px;
  width: 100px;
  border-width: 10px;
}

.table-container ::v-deep.table.b-table tbody td {
  font-size: 14px;
}
</style>
